/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";

import { NotifiedCountriesCode } from "../../../../config/filingConstants";
import { IAddIncomeForm } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { ContextAwareToggle } from "../../../accordion";
import { NumericInputField, SelectInputField } from "../../../fields";

const SalarySection89ANotifiedForm = () => {
	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();
	const [disableInput, setDisableInput] = useState(false);

	return (
		<Accordion className="accordion-sm" defaultActiveKey="gross-salary-1-d">
			<Accordion.Item eventKey="gross-salary-1-d">
				<ContextAwareToggle eventKey="gross-salary-1-d" isChild={true}>
					<div className="btn-content justify-content-between">
						<Form.Label className="leading-para mb-0">
							I(d). Income under section 89A - Notified Country
							<span
								className="info-wrap"
								role="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									dispatch(
										showHintModal({
											title: "Income covered u/s 89A",
											componentName: "Section89ANCHint"
										})
									);
								}}
							>
								<FASvgIcon icon={faInfoCircle} />
							</span>
						</Form.Label>
						<div
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
							className="flex-grow-1"
						>
							<NumericInputField
								showLeftIcon={true}
								name="IncomeUnderSection89ANotifiedCountry"
								value={values?.IncomeUnderSection89ANotifiedCountry || ""}
								onBlur={handleBlur("IncomeUnderSection89ANotifiedCountry")}
								onChange={(value) => setFieldValue("IncomeUnderSection89ANotifiedCountry", value)}
								isInvalid={
									Boolean(errors.IncomeUnderSection89ANotifiedCountry) &&
									touched.IncomeUnderSection89ANotifiedCountry
								}
								isDisabled={disableInput}
							/>
						</div>
					</div>
				</ContextAwareToggle>
				<Accordion.Body className="py-0">
					<GrossSalary1DBreakupForm handleOnSumChange={(val) => setDisableInput(() => val)} />
				</Accordion.Body>
			</Accordion.Item>
		</Accordion>
	);
};

export default SalarySection89ANotifiedForm;

type FormProps = { handleOnSumChange: (isDisabled: boolean) => void };

const GrossSalary1DBreakupForm: React.FC<FormProps> = (props: FormProps) => {
	const { handleOnSumChange } = props;

	const { values, setFieldValue } = useFormikContext<IAddIncomeForm>();

	const [sum, setSum] = useState(0);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

	useEffect(() => {
		let _sum = 0;
		values?.IncomeUnderSection89ADescriptions?.forEach((item) => {
			if (item.length && item[1]) {
				_sum += Number(item[1]) || 0;
				setIsFirstRender(() => false);
			}
		});

		setSum(() => _sum);

		// also update the gross total in upper section
		handleOnSumChange(_sum > 0);

		if (isFirstRender && !_sum) {
			setFieldValue(
				"IncomeUnderSection89ANotifiedCountry",
				values?.IncomeUnderSection89ANotifiedCountry || ""
			);
		} else if (!isFirstRender) {
			setFieldValue("IncomeUnderSection89ANotifiedCountry", _sum);
		} else if (isFirstRender && _sum) {
			setFieldValue("IncomeUnderSection89ANotifiedCountry", _sum);
		}
	}, [currentTime]);

	return (
		<>
			<Card className="more-info-card shadow-none">
				<Card.Header>
					<Card.Title>Break up of section 89A - notified country</Card.Title>
				</Card.Header>
				<Card.Body>
					<section>
						<FieldArray name="IncomeUnderSection89ADescriptions">
							{({ push, replace, remove, form }) => (
								<>
									{values?.IncomeUnderSection89ADescriptions &&
									values?.IncomeUnderSection89ADescriptions?.length > 0
										? values?.IncomeUnderSection89ADescriptions.map((_, index) => (
												<section className="mb-3 mb-sm-0" key={`salary-allowance-${index}`}>
													<Section89ABreakupForm
														index={index}
														form={form}
														onRemove={(itemIdx: number) => {
															if (values.IncomeUnderSection89ADescriptions.length === 1) {
																replace(itemIdx, ["", ""]);
															} else {
																remove(itemIdx);
															}

															setCurrentTime(new Date().getTime());
														}}
														handleAmountInc={() => setCurrentTime(new Date().getTime())}
													/>
												</section>
										  ))
										: null}
									<PreventEditComponent>
										<Button variant="link" className="btn-add-link" onClick={() => push(["", ""])}>
											<FASvgIcon icon={faCirclePlus} />
											Add more
										</Button>
									</PreventEditComponent>
								</>
							)}
						</FieldArray>
					</section>
				</Card.Body>
				<Card.Footer>
					<p className="total-content">
						<span className="total-label">Total:</span>
						<span className="total-amount">
							<PriceDisplay price={sum} />
						</span>
					</p>
				</Card.Footer>
			</Card>
		</>
	);
};

type Props = {
	index: number;
	form: FormikProps<IAddIncomeForm>;
	onRemove: (itemIdx: number) => void;
	handleAmountInc: () => void;
};

const Section89ABreakupForm: React.FC<Props> = (props: Props) => {
	const { index, onRemove, form, handleAmountInc } = props;

	const { setFieldValue, handleBlur, values, touched, errors } = form;

	return (
		<>
			<Row className="align-items-center">
				<Col sm={6}>
					<SelectInputField
						placeholder="Select other"
						name={`IncomeUnderSection89ADescriptions.${index}[0]`}
						label="Description"
						isClearable={false}
						options={UtilService.convertToArray(NotifiedCountriesCode)}
						value={{
							"label": NotifiedCountriesCode[values.IncomeUnderSection89ADescriptions[index][0]],
							"value": values.IncomeUnderSection89ADescriptions[index][0]
						}}
						onChange={(selectedStatus) => {
							if (selectedStatus) {
								setFieldValue(
									`IncomeUnderSection89ADescriptions.${index}[0]`,
									selectedStatus.value
								);
							} else {
								setFieldValue(`IncomeUnderSection89ADescriptions.${index}[0]`, undefined);
							}
						}}
						onBlur={handleBlur(`IncomeUnderSection89ADescriptions.${index}[0]`)}
						containerClasses={
							Boolean(getIn(errors, `IncomeUnderSection89ADescriptions.${index}[0]`)) &&
							getIn(touched, `IncomeUnderSection89ADescriptions.${index}[0]`)
						}
						errorMsg={getIn(errors, `IncomeUnderSection89ADescriptions.${index}[0]`)}
					/>
				</Col>
				<Col sm>
					<NumericInputField
						showLeftIcon={true}
						placeholder=""
						name={`IncomeUnderSection89ADescriptions.${index}[1]`}
						label="Amount"
						value={values.IncomeUnderSection89ADescriptions[index][1]}
						onBlur={handleBlur(`IncomeUnderSection89ADescriptions.${index}[1]`)}
						onChange={(value) => {
							setFieldValue(`IncomeUnderSection89ADescriptions.${index}[1]`, value);
							handleAmountInc();
						}}
						isInvalid={
							Boolean(getIn(errors, `IncomeUnderSection89ADescriptions.${index}[1]`)) &&
							getIn(touched, `IncomeUnderSection89ADescriptions.${index}[1]`)
						}
						errorMsg={getIn(errors, `IncomeUnderSection89ADescriptions.${index}[1]`)}
					/>
				</Col>
				<PreventEditComponent>
					<Col sm="auto" className="ps-0">
						<Button
							variant="transparent"
							className="text-greyDark icon-only d-none d-sm-flex"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
						</Button>
						<Button
							variant="transparent"
							className="btn-delete-full d-sm-none"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
							<span>Delete</span>
						</Button>
					</Col>
				</PreventEditComponent>
			</Row>
			{index !== values.IncomeUnderSection89ADescriptions.length - 1 && (
				<hr className="d-block d-sm-none my-4" />
			)}
		</>
	);
};
